import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Plus, X, Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PILLARS } from "@/data/pillars";

export default function PillarSetupWizard() {
  const { reload } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [drafts, setDrafts] = useState(() =>
    PILLARS.map((p) => ({
      pillar: p.name,
      id: p.id,
      color: p.color,
      icon: p.icon,
      title: "",
      definition: "",
      goals: [],
    }))
  );

  const pillar = PILLARS[step];
  const draft = drafts[step];
  const isLast = step === PILLARS.length - 1;
  const stepValid = draft.title.trim() && draft.definition.trim();
  const allValid = drafts.every((d) => d.title.trim() && d.definition.trim());

  const patch = (changes) =>
    setDrafts((d) => d.map((x, i) => (i === step ? { ...x, ...changes } : x)));

  const updateGoal = (idx, changes) =>
    patch({ goals: draft.goals.map((g, i) => (i === idx ? { ...g, ...changes } : g)) });

  const addGoal = () => patch({ goals: [...draft.goals, { text: "", target_date: "" }] });
  const removeGoal = (idx) => patch({ goals: draft.goals.filter((_, i) => i !== idx) });

  const finish = async () => {
    setSaving(true);
    try {
      const payload = {
        pillars: drafts.map((d) => ({
          pillar: d.pillar,
          title: d.title,
          definition: d.definition,
          goals: d.goals
            .filter((g) => g.text.trim())
            .map((g) => ({ text: g.text.trim(), target_date: g.target_date || null })),
        })),
      };
      await api.post("/pillars/setup", payload);
      toast.success("Your pillars are set up. Welcome in.");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex items-center justify-center p-4">
      <div
        className="w-full max-w-2xl bg-card rounded-2xl border border-border shadow-[0_10px_60px_rgba(0,0,0,0.12)] overflow-hidden flex flex-col max-h-[92vh]"
        data-testid="setup-wizard"
      >
        {/* Header */}
        <div className="px-8 pt-8 pb-5" style={{ borderTop: `4px solid ${pillar.color}` }}>
          <div className="flex items-center gap-2 text-muted-foreground mb-2">
            <Sparkles className="w-4 h-4" />
            <span className="text-[11px] uppercase tracking-[0.18em]">
              Set up your pillars · {step + 1} of {PILLARS.length}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: pillar.color }} />
            <h2 className="font-serif text-3xl text-foreground">{pillar.name}</h2>
          </div>
          {/* progress dots */}
          <div className="flex gap-1.5 mt-5">
            {PILLARS.map((p, i) => (
              <button
                key={p.id}
                onClick={() => setStep(i)}
                className="h-1.5 rounded-full transition-all duration-300"
                style={{
                  width: i === step ? "28px" : "12px",
                  backgroundColor: i <= step ? p.color : "rgba(0,0,0,0.1)",
                }}
                aria-label={`Go to ${p.name}`}
              />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="px-8 pb-4 overflow-y-auto quiet-scroll">
          <AnimatePresence mode="wait">
            <motion.div
              key={pillar.id}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.25 }}
            >
              <label className="block text-sm font-medium text-foreground/80 mb-1.5">Title</label>
              <input
                value={draft.title}
                onChange={(e) => patch({ title: e.target.value })}
                placeholder="A short, personal title for this pillar"
                data-testid="wizard-title"
                className="w-full px-3.5 py-2.5 rounded-md border border-input bg-white/70 focus:outline-none focus:border-foreground/40 focus:ring-1 focus:ring-foreground/20 transition-colors duration-200 mb-5"
              />

              <label className="block text-sm font-medium text-foreground/80 mb-1.5">
                Definition
              </label>
              <textarea
                value={draft.definition}
                onChange={(e) => patch({ definition: e.target.value })}
                placeholder="What does this pillar mean to you?"
                rows={4}
                data-testid="wizard-definition"
                className="w-full px-3.5 py-2.5 rounded-md border border-input bg-white/70 focus:outline-none focus:border-foreground/40 focus:ring-1 focus:ring-foreground/20 transition-colors duration-200 leading-relaxed resize-none mb-6"
              />

              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-foreground/80">
                  Goals <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <button
                  onClick={addGoal}
                  data-testid="wizard-add-goal"
                  className="flex items-center gap-1 text-sm text-foreground/70 hover:text-foreground"
                >
                  <Plus className="w-4 h-4" /> Add goal
                </button>
              </div>
              <div className="space-y-2">
                {draft.goals.map((g, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <input
                      value={g.text}
                      onChange={(e) => updateGoal(idx, { text: e.target.value })}
                      placeholder="A goal for this pillar"
                      data-testid={`wizard-goal-${idx}`}
                      className="flex-1 px-3 py-2 rounded-md border border-input bg-white/70 text-sm focus:outline-none focus:border-foreground/40 transition-colors duration-200"
                    />
                    <input
                      type="date"
                      value={g.target_date}
                      onChange={(e) => updateGoal(idx, { target_date: e.target.value })}
                      className="w-[150px] px-2 py-2 rounded-md border border-input bg-white/70 text-sm text-muted-foreground focus:outline-none focus:border-foreground/40 transition-colors duration-200"
                    />
                    <button
                      onClick={() => removeGoal(idx)}
                      className="p-2 text-muted-foreground hover:text-destructive transition-colors duration-200"
                      aria-label="Remove goal"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {draft.goals.length === 0 && (
                  <p className="text-sm text-muted-foreground italic">
                    No goals yet — you can add them here or later on the Goals page.
                  </p>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="px-8 py-5 border-t border-border flex items-center justify-between bg-secondary/30">
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0 || saving}
            data-testid="wizard-back"
            className="flex items-center gap-1.5 px-4 py-2 rounded-md text-foreground/80 hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-200"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>

          {isLast ? (
            <button
              onClick={finish}
              disabled={saving || !allValid}
              data-testid="wizard-finish"
              className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-foreground text-background font-medium hover:bg-foreground/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Finish setup
            </button>
          ) : (
            <button
              onClick={() => setStep((s) => Math.min(PILLARS.length - 1, s + 1))}
              disabled={saving || !stepValid}
              data-testid="wizard-next"
              className="flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-foreground text-background font-medium hover:bg-foreground/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
